import React from 'react';
import NewButtonGroup, {ButtonGroupButton} from './NewButtonGroup';
import ThumbUpLineIcon from '../../atoms/icons/ThumbUpLineIcon';
import ThumbDownLineIcon from '../../atoms/icons/ThumbDownLineIcon';
import PauseCircleLineIcon from '../../atoms/icons/PauseCircleLineIcon';
import MailCloseLineIcon from '../../atoms/icons/MailCloseLineIcon';
import MailLineIcon from '../../atoms/icons/MailLineIcon';
import { useTranslation } from 'react-i18next';
import CandidateFilterStatus from '../../../enums/candidateFilterStatus';
import styles from './CandidateSidePanelButtonGroup.module.css';
import UserUnfollowLineIcon from '../../atoms/icons/UserUnfollowLineIcon';

interface CandidateSidePanelButtonGroupProps {
    status: string;
    isListingTenant?: boolean;
    onApprove: () => void;
    onUnassignTenant: () => void;
    onRejectWithMessage: () => void;
    onMoveToWaitingList: () => void;
    onRejectWithoutMessage: () => void;
    onSendMessage: () => void;
}

const CandidateSidePanelButtonGroup = ({
    status,
    isListingTenant,
    onApprove,
    onUnassignTenant,
    onRejectWithMessage,
    onMoveToWaitingList,
    onRejectWithoutMessage,
    onSendMessage,
}: CandidateSidePanelButtonGroupProps) => {
    const { t } = useTranslation();

    const avaliableButtons = {
        ApproveButton: {
            text: t('candidate.action.approve'),
            variant: 'secondary-gray',
            icon: <ThumbUpLineIcon />,
            onClick: onApprove
        },
        MoveToWaitingListButton: {
            text: t('candidate.status.not_suitable'),
            variant: 'secondary-gray',
            className: `${styles.hideOnLargeScreen} ${styles.showOnSmallScreen}`,
            icon: <PauseCircleLineIcon />,
            onClick: onMoveToWaitingList
        },
        RejectWithMessageButton: {
            text: t('candidate.action.reject'),
            variant: 'secondary-gray',
            icon: <ThumbDownLineIcon />,
            onClick: onRejectWithMessage
        },
        RejectWithoutMessageButton: {
            text: t('candidate.action.reject.withoutMessage'),
            variant: 'secondary-gray',
            icon: <MailCloseLineIcon />,
            onClick: onRejectWithoutMessage
        },
        UnasssignTenantButton: {
            text: t('candidate.action.unassign'),
            variant: 'secondary-gray',
            icon: <UserUnfollowLineIcon/>,
            onClick: onUnassignTenant
        },
        SendMessageButton: {
            text: t('candidate.action.initiateMessage'),
            variant: 'secondary-gray',
            icon: <MailLineIcon />,
            onClick: onSendMessage
        },
    }

    const renderButtons = (buttons) => {
        let ret = buttons;

        if (isListingTenant) {
            ret = [ ...buttons, avaliableButtons.UnasssignTenantButton ];
        }

        return ret;
    }

    const buttons = [
        {
            ...avaliableButtons.ApproveButton,
            ...(status === CandidateFilterStatus.Suitable && { className: styles.suitable })
        },
        {
            ...avaliableButtons.MoveToWaitingListButton,
            className: `${styles.moveToWaitingList} ${
                status === CandidateFilterStatus.NotSuitable ? styles.notSuitable : ''
            }`
        },
        {
            ...avaliableButtons.RejectWithMessageButton,
            ...(status === CandidateFilterStatus.Rejected && { className: styles.rejected })
        }
    ];

    const dropdownButtons = [
        {
            ...avaliableButtons.MoveToWaitingListButton,
            text: t('bulk.dropdown.queue'),
            selected: status === CandidateFilterStatus.NotSuitable,
        },
        avaliableButtons.RejectWithoutMessageButton,
        avaliableButtons.SendMessageButton
    ];

    return (
        <NewButtonGroup
            buttons={buttons as ButtonGroupButton[]}
            dropdownButtons={renderButtons(dropdownButtons) as ButtonGroupButton[]}
        />
    );
}

export default CandidateSidePanelButtonGroup;
